import { Button, IconClose, useSmallToMediaQuery } from '@madpaws/design-system';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import React, { useEffect, useRef } from 'react';

import { ImageWithFallback } from '~/common/components/ImageWithFallback/ImageWithFallback';
import { isAppOpenedInNativeAppBrowser } from '~/common/utils/app';
import { useAuthenticationContext } from '~/components/AuthenticationProvider/useAuthenticationContext';
import {
  CREATE_MOBILE_APP_BANNER_EPPO_ID,
  CREATE_MOBILE_APP_BANNER_EPPO_ID_VALUES,
} from '~/components/Eppo/constants';
import { useEppoStringAssignment } from '~/components/Eppo/helpers';
import { trackEvent } from '~/components/analytics/analytics';
import {
  MOBILE_APP_DOWNLOAD_BANNER_CLOSED_EVENT_NAME,
  MOBILE_APP_DOWNLOAD_BANNER_CTA_CLICKED_EVENT_NAME,
  MOBILE_APP_DOWNLOAD_BANNER_VIEWED_EVENT_NAME,
} from '~/components/analytics/constants';

import styles from './MobileAppDownloadBanner.module.css';

import type { ReactElement } from 'react';

const MOBILE_BANNER_CLOSED_COOKIE_NAME = 'mobile_banner_closed';

const SCHEME = 'madpaws://';
const IOS_APP_STORE_URL = 'https://apps.apple.com/au/app/mad-paws-pet-sitting-service/id983899686';
const ANDROID_PACKAGE = 'au.com.madpaws';
const ANDROID_PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const PATH = 'home?ref=app_banner';
const APP_OPEN_FALLBACK_DELAY_MS = 1500;

type Device = 'ios' | 'android' | 'other';
type Navigate = (url: string) => void;

const { publicRuntimeConfig } = getConfig();

export const detectDevice = (): Device => {
  if (typeof navigator === 'undefined') {
    return 'other';
  }

  const ua = navigator.userAgent.toLowerCase();

  if (/android/.test(ua)) {
    return 'android';
  }

  const isIOS =
    /iphone|ipod|ipad/.test(ua) ||
    (/macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);

  return isIOS ? 'ios' : 'other';
};

const navigateTo = (url: string): void => {
  window.location.href = url;
};

export const openAppWithFallback = (
  appUrl: string,
  fallbackUrl: string,
  navigate: Navigate = navigateTo
): (() => void) => {
  let hasFinished = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let cleanup = (): void => undefined;

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      cleanup();
    }
  };

  cleanup = (): void => {
    if (hasFinished) {
      return;
    }

    hasFinished = true;

    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', cleanup);
  };

  fallbackTimer = setTimeout(() => {
    cleanup();
    navigate(fallbackUrl);
  }, APP_OPEN_FALLBACK_DELAY_MS);

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', cleanup);

  try {
    navigate(appUrl);
  } catch {
    cleanup();
    navigate(fallbackUrl);
  }

  return cleanup;
};

export const MobileAppDownloadBanner = (): ReactElement => {
  const { isUserLoggedIn } = useAuthenticationContext();
  const isMediumToViewport = useSmallToMediaQuery();
  const device = detectDevice();
  const eventProperties = React.useMemo(() => ({ device_type: device }), [device]);

  const [isBannerClosed, setIsBannerClosed] = React.useState(
    !!Cookies.get(MOBILE_BANNER_CLOSED_COOKIE_NAME)
  );
  const [isOpeningApp, setIsOpeningApp] = React.useState(false);

  const isMobileAppDownloadBannerEnabled =
    useEppoStringAssignment(CREATE_MOBILE_APP_BANNER_EPPO_ID) ===
    CREATE_MOBILE_APP_BANNER_EPPO_ID_VALUES.ON;
  const isFromNativeAppBrowser = isAppOpenedInNativeAppBrowser();
  const hasTrackedViewedEvent = useRef(false);
  const appOpenCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (
      !isMediumToViewport ||
      !isUserLoggedIn ||
      !isMobileAppDownloadBannerEnabled ||
      isBannerClosed ||
      isFromNativeAppBrowser
    ) {
      return;
    }

    if (hasTrackedViewedEvent.current) {
      return;
    }

    hasTrackedViewedEvent.current = true;
    trackEvent(MOBILE_APP_DOWNLOAD_BANNER_VIEWED_EVENT_NAME, eventProperties);
  }, [
    device,
    eventProperties,
    isUserLoggedIn,
    isBannerClosed,
    isFromNativeAppBrowser,
    isMediumToViewport,
    isMobileAppDownloadBannerEnabled,
  ]);

  useEffect(() => {
    const cleanupOnUnmount = (): void => {
      appOpenCleanup.current?.();
    };

    return cleanupOnUnmount;
  }, []);

  if (
    !isMediumToViewport ||
    !isUserLoggedIn ||
    !isMobileAppDownloadBannerEnabled ||
    isBannerClosed ||
    isFromNativeAppBrowser
  ) {
    return <></>;
  }

  const handleClose = (): void => {
    Cookies.set(MOBILE_BANNER_CLOSED_COOKIE_NAME, 'true');
    setIsBannerClosed(true);
    trackEvent(MOBILE_APP_DOWNLOAD_BANNER_CLOSED_EVENT_NAME, eventProperties);
  };

  const openApp = (): void => {
    if (isOpeningApp) {
      return;
    }

    setIsOpeningApp(true);
    trackEvent(MOBILE_APP_DOWNLOAD_BANNER_CTA_CLICKED_EVENT_NAME, eventProperties);
    const schemeUrl = PATH ? `${SCHEME}${PATH}` : SCHEME;

    appOpenCleanup.current?.();
    appOpenCleanup.current = null;

    if (device === 'android') {
      const intentUrl =
        `intent://${PATH}#Intent;` +
        `scheme=${SCHEME.replace('://', '')};` +
        `package=${ANDROID_PACKAGE};` +
        `S.browser_fallback_url=${encodeURIComponent(ANDROID_PLAY_STORE_URL)};` +
        `end`;

      appOpenCleanup.current = openAppWithFallback(intentUrl, ANDROID_PLAY_STORE_URL);
      return;
    }

    if (device === 'ios') {
      appOpenCleanup.current = openAppWithFallback(schemeUrl, IOS_APP_STORE_URL);
      return;
    }

    window.open(IOS_APP_STORE_URL, '_blank', 'noopener,noreferrer');
    setIsOpeningApp(false);
  };

  return (
    <div className={styles.root}>
      <div className={styles.leftSection}>
        <button
          aria-label="Close mobile app download banner"
          className={styles.closeButton}
          onClick={handleClose}
          type="button"
        >
          <IconClose size="small2x" />
        </button>
        <ImageWithFallback
          alt="Picture of a pet"
          fallback={`${publicRuntimeConfig.staticPath}/images/MobileBanner/logo.png`}
          loading="lazy"
          webpSrc={`${publicRuntimeConfig.staticPath}/images/MobileBanner/logo.png`}
          width="60px"
        />
        <div className={styles.textSection}>
          <p className={styles.title}>Mad Paws App</p>
          <ImageWithFallback
            alt="Picture of a pet"
            fallback={`${publicRuntimeConfig.staticPath}/images/MobileBanner/stars.png`}
            loading="lazy"
            webpSrc={`${publicRuntimeConfig.staticPath}/images/MobileBanner/stars.png`}
            width="60px"
          />
          <p>4.4K Reviews</p>
        </div>
      </div>
      <Button isDisabled={isOpeningApp} label="Open" onClick={openApp} variant="primary" />
    </div>
  );
};
